// Authentication and SharePoint Configuration

export type EnvName = 'production' | 'staging' | 'development';

// Build environment, stamped by CI via VITE_APP_ENV. Unknown/missing values
// resolve to 'development' so only an explicit 'production'/'staging' counts.
export function resolveEnvName(raw?: string): EnvName {
  if (raw === 'production') return 'production';
  if (raw === 'staging') return 'staging';
  return 'development';
}

export const config = {
  // Build identity, stamped by CI: release tag (YYYY.M.D.HHMM) and commit.
  // 'dev' for local builds, 'staging' for main-branch deploys.
  build: {
    version: import.meta.env.VITE_APP_VERSION || 'dev',
    sha: (import.meta.env.VITE_APP_SHA || '').slice(0, 7),
    env: resolveEnvName(import.meta.env.VITE_APP_ENV),
    get isProd() {
      return this.env === 'production';
    },
  },
  // Application Insights (client-side). Empty string disables telemetry (local
  // dev). Staging and prod share one resource; events are tagged by env.
  telemetry: {
    connectionString: import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING || '',
  },
  // Microsoft Entra ID (used for MSAL.js Nested App Authentication)
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    // Derived from the serving origin so the same bundle works on localhost,
    // the SWA default hostname, and the custom domain. Every origin must be
    // registered on the Entra app (https + the two brk- broker schemes).
    redirectUri: `${window.location.origin}/taskpane.html`,
    // Delegated Microsoft Graph scopes requested for SharePoint + mail access.
    scopes: [
      'Files.ReadWrite.All',
      'Sites.ReadWrite.All',
      'User.Read',
      'Mail.Read',
      'Mail.ReadWrite',
    ],
    // Scopes used specifically for SharePoint drive operations via Graph.
    graphScopes: ['Files.ReadWrite.All', 'Sites.ReadWrite.All'],
  },

  // SharePoint storage backend (Microsoft Graph)
  sharePoint: {
    // Default Graph site id (used when no site is selected, and by tests).
    siteId: import.meta.env.VITE_SP_SITE_ID || '',
    // The CRG sites the add-in can browse. The home view lists the ones the
    // signed-in user can actually access. Composite ids are not secrets; edit
    // here to add sites (or wire VITE_SP_SITES later).
    sites: [
      {
        id: 'appriver3651000656.sharepoint.com,3e1ccc99-2dda-4b21-99e2-fd12c9ef900c,86f234c1-6e49-4b8d-b7d1-9876f5c48b6c',
        name: 'Full Service',
        webUrl: 'https://appriver3651000656.sharepoint.com/sites/FullService',
      },
      {
        id: 'appriver3651000656.sharepoint.com,01c78acd-8a50-4ec1-b613-34e555ce5ae9,86f234c1-6e49-4b8d-b7d1-9876f5c48b6c',
        name: 'Field Department',
        webUrl: 'https://appriver3651000656.sharepoint.com/sites/FieldDepartment',
      },
      {
        id: 'appriver3651000656.sharepoint.com,55118647-b64f-4783-b90a-3f8c8313dc30,fc2cb841-07bf-4e07-b82d-a8e5a4a9a3f2',
        name: 'ECI Corp',
        webUrl: 'https://appriver3651000656.sharepoint.com/sites/ecicorp',
      },
    ] as Array<{ id: string; name: string; webUrl: string }>,
    // Delegated SharePoint REST scope (separate token audience from Graph),
    // used only for the lock-tolerant list-item rename that Graph can't do.
    restScope: 'https://appriver3651000656.sharepoint.com/AllSites.Write',
  },

  // Azure AI Search (optional, for metadata search)
  search: {
    endpoint: import.meta.env.VITE_SEARCH_ENDPOINT || '',
    apiKey: import.meta.env.VITE_SEARCH_API_KEY || '',
    indexName: import.meta.env.VITE_SEARCH_INDEX_NAME || 'public-folders-index',
  },

  // Feature flags
  features: {
    enableSearch: true,
    enablePreview: true,
    enableDragDrop: true,
    maxUploadSize: 250 * 1024 * 1024 * 1024, // 250 GB - matches SharePoint Online's per-file limit (no lower add-in cap)
    maxPreviewSize: 10 * 1024 * 1024, // 10MB
    // ContentHash is computed by reading the whole file into memory, which fails
    // for multi-GB files (browser ArrayBuffer limit ~2 GB). Skip hashing above
    // this size; the upload itself streams in chunks regardless.
    maxHashSize: 256 * 1024 * 1024, // 256 MB
  },
};

// File type configurations
export const fileTypes = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'],
  video: ['mp4', 'webm', 'avi', 'mov', 'wmv', 'mkv'],
  audio: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'],
  document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'],
  text: ['txt', 'md', 'rtf', 'csv', 'json', 'xml'],
  code: ['js', 'ts', 'tsx', 'jsx', 'html', 'css', 'scss', 'py', 'java', 'cs', 'cpp', 'c', 'h'],
  email: ['eml', 'msg'],
  archive: ['zip', 'rar', '7z', 'tar', 'gz'],
} as const;

// Get file extension
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

// Get file category
export function getFileCategory(filename: string): string {
  const ext = getFileExtension(filename);

  for (const [category, extensions] of Object.entries(fileTypes)) {
    if ((extensions as readonly string[]).includes(ext)) {
      return category;
    }
  }

  return 'unknown';
}

// Format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Format date - compact for list view
export function formatDate(date: Date, timeZone?: string): string {
  // Always the same all-digit shape (MM/DD/YYYY) so the Date column is
  // visually consistent; the row tooltip (formatDateFull) carries the time.
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone,
  }).format(date);
}

// Format date - full format for preview/tooltip
export function formatDateFull(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(date);
}

export interface ItemDateDisplay {
  date: Date;
  source: 'Received' | 'Modified';
}

export function getItemDateDisplay(item: {
  name: string;
  isFolder: boolean;
  lastModified?: Date;
  emailReceivedDate?: Date;
}): ItemDateDisplay | undefined {
  if (!item.isFolder && isEmailFile(item.name) && item.emailReceivedDate) {
    return { date: item.emailReceivedDate, source: 'Received' };
  }
  return item.lastModified ? { date: item.lastModified, source: 'Modified' } : undefined;
}

export function formatItemDateTooltip(display: ItemDateDisplay, timeZone?: string): string {
  return `${display.source}: ${formatDateFull(display.date, timeZone)}`;
}

// Make a name SharePoint/OneDrive will accept. They reject " * : < > ? / \ |
// (and control chars), and a name cannot start with a space or end with a space
// or dot. A colon also breaks Graph's root:/path: addressing. Replace the
// invalid characters with "_" (matching Migration Manager). Single source of
// truth - every save path (email subject, drag-drop, upload) uses this.
export function sanitizeFileName(name: string): string {
  return name
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/^ +/, '')
    .replace(/[ .]+$/, '');
}

// Generate a safe filename from an email subject. No timestamp: SharePoint
// resolves name collisions itself (conflictBehavior=rename appends 1, 2, ...).
export function generateEmailFileName(subject: string, extension: string = 'eml'): string {
  const safe =
    sanitizeFileName(subject)
      .slice(0, 100)
      .replace(/[ .]+$/, '') || 'No Subject';
  return `${safe}.${extension}`;
}

// Check if file is an email
export function isEmailFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return fileTypes.email.includes(ext as 'eml' | 'msg');
}

// Get content type from extension
export function getContentType(filename: string): string {
  const ext = getFileExtension(filename);
  const contentTypes: Record<string, string> = {
    // Emails
    eml: 'message/rfc822',
    msg: 'application/vnd.ms-outlook',
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    // Audio/Video
    mp3: 'audio/mpeg',
    mp4: 'video/mp4',
    // Text
    txt: 'text/plain',
    html: 'text/html',
    css: 'text/css',
    js: 'text/javascript',
    json: 'application/json',
    xml: 'application/xml',
  };

  return contentTypes[ext] || 'application/octet-stream';
}
